/**
 * openai.js — AI utility functions (client-side)
 * All calls go through the Supabase Edge Function — no API keys or prompts in the browser.
 */

import { supabase, AI_FUNCTION } from './supabaseClient';

/**
 * Transcribe audio using Whisper (via Edge Function).
 * Auto-detected language wird mit zurückgegeben (z.B. "polish", "german") —
 * für mehrsprachige Eingabe (polnischer Helfer spricht polnisch ein).
 * @param {Blob} audioBlob — The audio blob to transcribe
 * @returns {{ text: string, language: string }}
 */
export async function transcribeAudio(audioBlob) {
    const formData = new FormData();
    const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
    formData.append('file', audioBlob, `recording.${ext}`);
    formData.append('action', 'transcribe');

    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: formData,
    });

    if (error) throw new Error(error.message || 'Transkription fehlgeschlagen.');
    if (data?.error) throw new Error(data.error);
    return { text: data.text, language: data.language || 'german' };
}

/**
 * Generate an offer from a transcript (via Edge Function).
 */
export async function generateOfferFromTranscript(transcript) {
    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: { action: 'generate-offer', transcript },
    });

    if (error) throw new Error(error.message || 'Angebotserstellung fehlgeschlagen.');
    if (data?.error) throw new Error(data.error);
    return data;
}

/**
 * Extract Posten from a Begehung consolidation (via Edge Function).
 */
export async function extractPostenFromBegehung(consolidation) {
    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: { action: 'extract-posten', consolidation },
    });

    if (error) return { error: error.message || 'Extraktion fehlgeschlagen.' };
    if (data?.error) return { error: data.error };
    return data;
}

/**
 * Review a Kalkulation and return AI suggestions (via Edge Function).
 */
export async function reviewKalkulation({ posten, gesamtNetto, projektName }) {
    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: { action: 'review-kalkulation', posten, gesamtNetto, projektName },
    });
    if (error) throw new Error(error.message || 'Review fehlgeschlagen.');
    if (data?.error) throw new Error(data.error);
    return data;
}

/**
 * KI-Disposition: 3 Slot-Vorschläge für ein neues Projekt.
 * @param {Object} params
 * @param {Object} params.project — { name, type, estimated_hours, client_name, client_address, today }
 * @param {Array}  params.team    — [{ id, name, rolle, rate_eur_h, past_projects_same_type, busy_periods }]
 * @returns {{ suggestions: Array<{ title, start_date, end_date, mitarbeiter_ids, reasoning }> }}
 */
export async function suggestDispatchSlots({ project, team, fahrzeuge = [], regeln = [] }) {
    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: { action: 'suggest-dispatch-slots', project, team, fahrzeuge, regeln },
    });
    if (error) throw new Error(error.message || 'KI-Disposition fehlgeschlagen.');
    if (data?.error) throw new Error(data.error);
    return { suggestions: data.suggestions || [] };
}

/**
 * Refine a title or description (via Edge Function).
 */
export async function refineTitleAndDescription({ title, description, prompt, type }) {
    const { data, error } = await supabase.functions.invoke(AI_FUNCTION, {
        body: {
            action: 'refine-title',
            description: description || title,
            customPrompt: prompt,
            type: type || 'description',
        },
    });

    if (error) return '';
    if (data?.error) return '';
    return type === 'title' ? data.subject : data.description;
}
